import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { faEllipsis, faPlus } from "@fortawesome/pro-regular-svg-icons";

import {
	ButtonStandard,
	Chip,
	DropDownMenu,
	Icon,
	Pagination,
	Skeleton,
	Table,
	Text,
	toast,
} from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";
import type { Role } from "@performyard/service-contracts";

import { AdminTitle } from "../../../../components/Admin";
import FilterChips from "../../../../components/FilterChips";
import ArchiveModal from "./ArchiveModal";
import RolesFilterPanel from "./RolesFilterPanel";

import { rolesKeys, useArchiveRole, useReopenRole, useRoles } from "../../../../api/hooks/defs/roles";
import useNavigate from "../../../../hooks/useNavigate";

import { ErrorResponse } from "../../../../api/services/defs/internal-api/internal-base-request";

import { convertFiltersToFilterChips, getNonDismissibleChipIds, getRolesListParams } from "./utils";

import StandardToolbarActionRow from "../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { DEFAULT_FILTERS, ROLES_PAGE_SIZE, type RolesFilters } from "./schema";

const activeOptions = [
	{ key: "edit", label: "Edit" },
	{ key: "archive", label: "Archive" },
];

const archivedOptions = [{ key: "reopen", label: "Reopen" }];

const isLoadingRow = (data: Partial<Role>) => !data._id;

const RolesTab = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [archiveTarget, setArchiveTarget] = useState<Role | null>(null);
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

	const {
		filters: rawFilters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<RolesFilters>({
		defaultSortName: "name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
		fallbackFilters: DEFAULT_FILTERS,
	});

	const filters: RolesFilters = { ...DEFAULT_FILTERS, ...rawFilters };

	const listParams = getRolesListParams(filters, debouncedSearchValue, sort);
	const { data, isLoading } = useRoles(
		{
			...listParams,
			limit: ROLES_PAGE_SIZE,
			skip: (pageNumber - 1) * ROLES_PAGE_SIZE,
		},
		{ placeholderData: (previousData) => previousData }
	);

	const roles = data?.roles ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / ROLES_PAGE_SIZE);

	const chips = convertFiltersToFilterChips(filters);
	const nonDismissibleChipIds = getNonDismissibleChipIds(filters);

	const { mutate: archiveMutate } = useArchiveRole({
		onSuccess: () => {
			toast.success("Role Archived");
			setArchiveTarget(null);
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
		},
		onError: (error) => {
			if (error instanceof ErrorResponse && error.data?.message === "role_has_assigned_employees") {
				const assignedCount = Number(error.data?.meta?.assignedCount);
				setArchiveTarget((prev) => (prev ? { ...prev, employeeCount: assignedCount } : null));
				return;
			}
			toast.error(error instanceof Error ? error.message : "An error occurred while archiving the role.");
			setArchiveTarget(null);
		},
	});

	const { mutate: reopenMutate } = useReopenRole({
		onSuccess: () => {
			toast.success("Role Reopened");
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : "An error occurred while reopening the role.");
		},
	});

	const handleAction = (key: string, row: Role) => {
		switch (key) {
			case "edit":
				navigate(`/administration/roles/${row._id}/edit`);
				break;
			case "archive":
				setArchiveTarget(row);
				break;
			case "reopen":
				reopenMutate(row._id);
				break;
		}
	};

	const onSortChanged = (event: SortChangedEvent<Role>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const handleDeleteFilter = (id: string) => {
		if (id === "status") {
			setFilters({ ...filters, status: DEFAULT_FILTERS.status });
		}
	};

	const columns: PYColumnDef<Role>[] = [
		{
			colId: "name",
			headerName: "Role",
			width: 200,
			sortable: true,
			cellRenderer: (params: { data: Role }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				// Defaults to the Competencies tab — RoleDetail renders that tab when ?tab is absent.
				return (
					<Link
						to={`/administration/roles/${row._id}`}
						className="text-primary-5 no-underline hover:text-primary-7 hover:underline"
					>
						<Text size="paragraph-3" weight="regular">
							<span className="py-line-clamp-1">{row.name}</span>
						</Text>
					</Link>
				);
			},
		},
		{
			colId: "description",
			headerName: "Description",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: Role }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-2">{row.description}</span>
					</Text>
				);
			},
		},
		{
			colId: "employees",
			headerName: "Employees",
			width: 160,
			sortable: true,
			headerClass: "[&_.ag-header-cell-label]:justify-center",
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
			valueGetter: (params: { data?: Role }) => params.data?.employeeCount ?? 0,
			cellRenderer: (params: { data: Role }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-6" />;
				return row.employeeCount;
			},
		},
		{
			colId: "competencies",
			headerName: "Competencies",
			width: 160,
			sortable: true,
			headerClass: "[&_.ag-header-cell-label]:justify-center",
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
			valueGetter: (params: { data?: Role }) => params.data?.competencies?.length ?? 0,
			cellRenderer: (params: { data: Role }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-6" />;
				return row.competencies?.length ?? 0;
			},
		},
		{
			colId: "status",
			headerName: "Status",
			sortable: true,
			width: 100,
			cellStyle: { display: "flex", alignItems: "center" },
			// ag-grid's client comparator can't read a value from <Chip> JSX, so it leaves the
			// row order untouched after a Status header click. valueGetter exposes the raw
			// status string so the visual reorder matches what the server returns.
			valueGetter: (params: { data?: Role }) => params.data?.status ?? "",
			cellRenderer: (params: { data: Role }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-5 w-14 rounded-full" />;
				return row.status === "active" ? (
					<Chip label="Active" color="primary" size={20} />
				) : (
					<Chip label="Archived" color="neutral" size={20} />
				);
			},
		},
		{
			colId: "actions",
			headerName: "",
			headerClass: "!pr-1",
			cellClass: "!pr-1",
			sortable: false,
			width: 50,
			resizable: false,
			suppressSizeToFit: true,
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
			cellRenderer: (params: { data: Role }) => {
				const row = params.data;
				if (isLoadingRow(row)) return null;
				return (
					<DropDownMenu
						options={row.status === "active" ? activeOptions : archivedOptions}
						onSelect={(key) => handleAction(key, row)}
						placement="bottom-end"
						minWidth={80}
					>
						<Icon icon={faEllipsis} size={24} color="neutral-5" />
					</DropDownMenu>
				);
			},
		},
	];

	const rolesLength = isLoading ? 3 : roles.length;
	const tableWrapperHeight = rolesLength === 0 ? "356px" : `${44 + rolesLength * 48}px`;

	return (
		<div>
			<AdminTitle includeSpacer={false}>Roles</AdminTitle>

			<div className="mb-2 flex items-center justify-between gap-4">
				<div className="min-w-0 flex-1">
					<StandardToolbarActionRow
						searchInput={searchValue}
						onSearchInputChange={(value) => setDebouncedSearch(value)}
						searchPlaceholder="Search roles..."
						onFilterButtonPress={() => setIsFilterPanelOpen(true)}
						activeFiltersCount={chips.length}
					/>
				</div>
				<div>
					<ButtonStandard
						color="primary"
						iconLeft={faPlus}
						onClick={() => navigate("/administration/roles/create")}
					>
						Create Role
					</ButtonStandard>
				</div>
			</div>

			<div className="mb-2">
				<FilterChips
					filters={chips}
					label="Filters applied: "
					nonDismissibleFilters={nonDismissibleChipIds}
					onDeleteFilter={handleDeleteFilter}
				/>
			</div>

			<div
				style={{
					height: tableWrapperHeight,
					transition: "height 150ms ease-out",
				}}
			>
				<Table<Role>
					columnDefs={columns}
					rowData={roles}
					loading={isLoading}
					getRowId={(params) => params.data._id}
					rowHeight={48}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						resizable: false,
						sortingOrder: ["asc", "desc"],
					}}
					emptyStateText="No Roles Found"
					onSortChanged={onSortChanged}
					initialState={{
						sort: {
							sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction }] : [],
						},
					}}
				/>
			</div>

			{pageCount > 1 && (
				<div className="mt-4 flex justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}

			<RolesFilterPanel
				isOpen={isFilterPanelOpen}
				onClose={() => setIsFilterPanelOpen(false)}
				filters={filters}
				onApplyFilters={(newFilters) => setFilters(newFilters)}
			/>

			{archiveTarget && (
				<ArchiveModal
					isOpen
					roleName={archiveTarget.name}
					employeeCount={archiveTarget.employeeCount}
					onConfirm={() => archiveMutate(archiveTarget._id)}
					onClose={() => setArchiveTarget(null)}
					onViewEmployees={() => navigate(`/administration/roles/${archiveTarget._id}?tab=employees`)}
				/>
			)}
		</div>
	);
};

export default RolesTab;
